import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import adminApi from '../utils/adminApi';

const SERVICE_LABELS = {
  documents: 'Documents',
  copies: 'Photocopies',
  passport: 'Passport Photos',
  lamination: 'Lamination',
  binding: 'Binding',
  business_cards: 'Business Cards',
  posters: 'Posters / Flyers',
  scanning: 'Scanning',
  fax: 'Fax Service',
  custom: 'Custom Prints',
};

const STATUS_STYLES = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  ready: { label: 'Ready', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  completed: { label: 'Completed', color: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
};

const STATUS_OPTIONS = ['pending', 'in_progress', 'ready', 'completed', 'cancelled'];

// Admin's own WhatsApp — files can be forwarded here for printing/downloading on phone
const ADMIN_WHATSAPP_NUMBER = '31614359710';

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const PrintJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.get('/print-jobs');
      setJobs(res.data || []);
    } catch (err) {
      toast.error('Could not load print jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/print-jobs/${id}`, { status });
      setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, status } : j)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success(`Marked as ${STATUS_STYLES[status]?.label || status}`);
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const filtered = statusFilter === 'all' ? jobs : jobs.filter((j) => j.status === statusFilter);
  const counts = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: jobs.filter((j) => j.status === s).length }), {});

  const openWhatsApp = (job) => {
    // strip everything non-digit; if starts with 0 assume Dutch number
    let n = (job.customer_phone || '').replace(/\D/g, '');
    if (n.startsWith('0')) n = '31' + n.slice(1);
    if (!n) { toast.error('No phone number on record'); return; }
    const msg = `Hi ${job.customer_name}! Your print order ${job.order_number} — `;
    window.open(`https://wa.me/${n}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Open a file in a new tab and trigger the browser's print dialog
  const printFile = (file) => {
    const ext = (file.name || file.url || '').toLowerCase();
    const isImage = /\.(jpg|jpeg|png|webp|gif|heic)$/.test(ext);
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Popup blocked — allow popups for this site');
      return;
    }
    if (isImage) {
      w.document.write(`
        <html>
          <head>
            <title>Print — ${file.name || 'file'}</title>
            <style>
              body { margin: 0; display: flex; align-items: center; justify-content: center; min-height: 100vh; }
              img { max-width: 100%; max-height: 100vh; }
              @media print {
                body { min-height: auto; }
                img { max-height: none; page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <img src="${file.url}" onload="setTimeout(() => window.print(), 300)" />
          </body>
        </html>
      `);
      w.document.close();
    } else {
      // For PDFs and other document types, embed in iframe and print
      w.document.write(`
        <html>
          <head>
            <title>Print — ${file.name || 'file'}</title>
            <style>
              body { margin: 0; }
              iframe { width: 100vw; height: 100vh; border: 0; }
            </style>
          </head>
          <body>
            <iframe src="${file.url}" onload="setTimeout(() => { try { this.contentWindow.print(); } catch(e) { window.print(); } }, 800)"></iframe>
          </body>
        </html>
      `);
      w.document.close();
    }
    toast.success('Opening print dialog...');
  };

  // Send a single file URL to admin's own WhatsApp for printing/downloading on phone
  const sendFileToMyWhatsApp = (file, job) => {
    const msg = `📄 Print job ${job.order_number} — ${job.customer_name}\nFile: ${file.name}\n${file.url}`;
    window.open(`https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Send all files at once to admin's own WhatsApp
  const sendAllFilesToMyWhatsApp = (job) => {
    if (!job.files || job.files.length === 0) return;
    const lines = [
      `📄 Print job ${job.order_number} — ${job.customer_name}`,
      `Service: ${SERVICE_LABELS[job.service_type] || job.service_type} · Qty: ${job.quantity}`,
      job.print_options?.color ? `Color: ${job.print_options.color === 'color' ? 'Color' : 'B&W'}` : null,
      job.print_options?.duplex != null ? `Sides: ${job.print_options.duplex ? 'Double' : 'Single'}` : null,
      job.print_options?.paper_size ? `Paper: ${job.print_options.paper_size}` : null,
      job.notes ? `Notes: ${job.notes}` : null,
      '',
      'Files:',
      ...job.files.map((f, i) => `${i + 1}. ${f.name}\n${f.url}`),
    ].filter(Boolean);
    window.open(`https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <button
            onClick={() => navigate('/staff')}
            className="inline-flex items-center space-x-2 px-5 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-md border border-gray-200"
            data-testid="back-to-admin-btn"
          >
            <i className="fas fa-arrow-left"></i>
            <span>Back to Admin</span>
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 shadow-md"
            data-testid="refresh-jobs-btn"
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i> Refresh
          </button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <i className="fas fa-print text-orange-500"></i>
            Print Inquiries
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Every print request customers submit lands here.</p>
        </div>

        {/* Status filter tabs */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-2 mb-6 flex flex-wrap gap-1" data-testid="status-tabs">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${statusFilter === 'all' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            data-testid="tab-all"
          >
            All <span className="ml-1 opacity-70">({jobs.length})</span>
          </button>
          {STATUS_OPTIONS.map((s) => {
            const style = STATUS_STYLES[s];
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${statusFilter === s ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                data-testid={`tab-${s}`}
              >
                <span className={`w-2 h-2 rounded-full ${style.dot}`}></span>
                {style.label} <span className="opacity-70">({counts[s]})</span>
              </button>
            );
          })}
        </div>

        {/* Job list */}
        {loading ? (
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-12 text-center text-gray-400">
            <i className="fas fa-spinner fa-spin text-3xl mb-3"></i>
            <p>Loading print jobs...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-12 text-center" data-testid="empty-state">
            <i className="fas fa-inbox text-5xl text-gray-300 mb-3"></i>
            <p className="text-gray-500 font-semibold">No print jobs yet</p>
            <p className="text-gray-400 text-sm mt-1">Customer inquiries will show up here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3" data-testid="jobs-list">
            {filtered.map((job) => {
              const style = STATUS_STYLES[job.status] || STATUS_STYLES.pending;
              return (
                <div
                  key={job.id}
                  className={`bg-white rounded-2xl shadow-md border-l-4 border border-gray-100 p-5 hover:shadow-lg transition-all cursor-pointer ${
                    job.status === 'pending' ? 'border-l-amber-500' :
                    job.status === 'in_progress' ? 'border-l-blue-500' :
                    job.status === 'ready' ? 'border-l-emerald-500' :
                    job.status === 'cancelled' ? 'border-l-red-500' : 'border-l-gray-400'
                  }`}
                  onClick={() => setSelected(job)}
                  data-testid={`job-row-${job.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono font-bold text-gray-800 text-sm">{job.order_number}</span>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${style.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}></span>{style.label}
                        </span>
                        {job.urgent && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-orange-100 text-orange-700 border border-orange-200">
                            <i className="fas fa-bolt"></i> RUSH
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{timeAgo(job.created_at)}</span>
                      </div>
                      <p className="text-lg font-bold text-gray-800">{job.customer_name || '—'}</p>
                      <p className="text-sm text-gray-500">
                        {SERVICE_LABELS[job.service_type] || job.service_type} · Qty {job.quantity} · {job.fulfillment === 'pickup' ? 'Pickup' : 'Delivery'} · Pay {job.payment_method === 'in_store' ? 'in store' : 'online'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        <i className="fas fa-phone mr-1"></i>{job.customer_phone}
                        {job.customer_email && <span className="ml-3"><i className="fas fa-envelope mr-1"></i>{job.customer_email}</span>}
                        {job.files?.length > 0 && <span className="ml-3"><i className="fas fa-paperclip mr-1"></i>{job.files.length} file{job.files.length > 1 ? 's' : ''}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => { e.stopPropagation(); openWhatsApp(job); }} className="w-9 h-9 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg flex items-center justify-center" title="WhatsApp customer">
                        <i className="fab fa-whatsapp"></i>
                      </button>
                      <i className="fas fa-chevron-right text-gray-300"></i>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="job-detail-drawer">
          <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={() => setSelected(null)}></div>
          <div className="w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">Print inquiry</p>
                <p className="font-mono font-bold text-gray-800">{selected.order_number}</p>
              </div>
              <button onClick={() => setSelected(null)} className="w-9 h-9 rounded-full hover:bg-gray-100 text-gray-500">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Status */}
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Status</p>
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_OPTIONS.map((s) => {
                    const st = STATUS_STYLES[s];
                    return (
                      <button
                        key={s}
                        onClick={() => updateStatus(selected.id, s)}
                        className={`px-3 py-2 rounded-lg text-xs font-bold border-2 flex items-center gap-2 ${selected.status === s ? `${st.color} border-current` : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                        data-testid={`set-status-${s}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${st.dot}`}></span>
                        {st.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Customer */}
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Customer</p>
                <div className="bg-gray-50 rounded-xl p-4 space-y-1.5">
                  <p className="font-bold text-gray-800">{selected.customer_name || '—'}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-2"><i className="fas fa-phone text-gray-400"></i> {selected.customer_phone}</p>
                  {selected.customer_email && <p className="text-sm text-gray-600 flex items-center gap-2"><i className="fas fa-envelope text-gray-400"></i> {selected.customer_email}</p>}
                  <button onClick={() => openWhatsApp(selected)} className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg">
                    <i className="fab fa-whatsapp"></i> Message on WhatsApp
                  </button>
                </div>
              </div>

              {/* Order */}
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">What they want</p>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-400">Service</span><span className="font-bold text-gray-800">{SERVICE_LABELS[selected.service_type] || selected.service_type}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Quantity</span><span className="font-bold text-gray-800">{selected.quantity}</span></div>
                  {selected.print_options?.color && <div className="flex justify-between"><span className="text-gray-400">Color</span><span className="font-bold text-gray-800">{selected.print_options.color === 'color' ? 'Color' : 'B&W'}</span></div>}
                  {selected.print_options?.duplex != null && <div className="flex justify-between"><span className="text-gray-400">Sides</span><span className="font-bold text-gray-800">{selected.print_options.duplex ? 'Double' : 'Single'}</span></div>}
                  {selected.print_options?.paper_size && <div className="flex justify-between"><span className="text-gray-400">Paper size</span><span className="font-bold text-gray-800">{selected.print_options.paper_size}</span></div>}
                  {selected.print_options?.paper_type && <div className="flex justify-between"><span className="text-gray-400">Paper type</span><span className="font-bold text-gray-800">{selected.print_options.paper_type}</span></div>}
                  {selected.print_options?.binding && selected.print_options.binding !== 'None' && <div className="flex justify-between"><span className="text-gray-400">Binding</span><span className="font-bold text-gray-800">{selected.print_options.binding}</span></div>}
                  <div className="flex justify-between"><span className="text-gray-400">Fulfillment</span><span className="font-bold text-gray-800">{selected.fulfillment === 'pickup' ? 'Pickup' : 'Delivery'}</span></div>
                  {selected.fulfillment === 'delivery' && selected.delivery_address && <div className="flex justify-between gap-2"><span className="text-gray-400 flex-shrink-0">Address</span><span className="font-bold text-gray-800 text-right">{selected.delivery_address}</span></div>}
                  <div className="flex justify-between"><span className="text-gray-400">Payment</span><span className="font-bold text-gray-800">{selected.payment_method === 'in_store' ? 'In store' : 'Online'}</span></div>
                  {selected.urgent && <div className="flex items-center gap-2 pt-2 text-orange-600 font-bold"><i className="fas fa-bolt"></i> RUSH ORDER</div>}
                </div>
              </div>

              {/* Notes */}
              {selected.notes && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Customer notes</p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-line">
                    {selected.notes}
                  </div>
                </div>
              )}

              {/* Files */}
              {selected.files?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-bold">Uploaded files ({selected.files.length})</p>
                    <button
                      onClick={() => sendAllFilesToMyWhatsApp(selected)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg"
                      data-testid="send-all-whatsapp-btn"
                    >
                      <i className="fab fa-whatsapp"></i> Send all to my phone
                    </button>
                  </div>
                  <div className="space-y-2">
                    {selected.files.map((f, i) => {
                      const isImage = /\.(jpg|jpeg|png|webp|gif|heic)$/i.test(f.name || '');
                      return (
                        <div key={i} className="p-3 bg-white border border-gray-200 rounded-xl" data-testid={`file-row-${i}`}>
                          <div className="flex items-center gap-3">
                            {isImage ? (
                              <button
                                onClick={() => setLightbox(f.url)}
                                className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 hover:ring-2 hover:ring-orange-400"
                              >
                                <img src={f.url} alt={f.name} className="w-full h-full object-cover" />
                              </button>
                            ) : (
                              <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="fas fa-file-lines text-xl"></i>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-800 truncate text-sm">{f.name}</p>
                              <p className="text-xs text-gray-400">{f.size ? `${(f.size / 1024).toFixed(0)} KB` : ''}</p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-3 gap-1.5">
                            <button
                              onClick={() => printFile(f)}
                              className="px-2 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1"
                              data-testid={`print-file-${i}`}
                              title="Open and print"
                            >
                              <i className="fas fa-print"></i> Print
                            </button>
                            <button
                              onClick={() => sendFileToMyWhatsApp(f, selected)}
                              className="px-2 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1"
                              data-testid={`whatsapp-file-${i}`}
                              title="Send to my WhatsApp"
                            >
                              <i className="fab fa-whatsapp"></i> To phone
                            </button>
                            <a
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={f.name}
                              className="px-2 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg flex items-center justify-center gap-1"
                              data-testid={`open-file-${i}`}
                              title="Open in new tab"
                            >
                              <i className="fas fa-external-link"></i> Open
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-400 text-center pt-4 border-t border-gray-100">
                Submitted {new Date(selected.created_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white hover:bg-white/30"><i className="fas fa-times"></i></button>
          <img src={lightbox} alt="File" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
};

export default PrintJobs;
